// Cookie Consent Management Utility
// Stores consent in localStorage (persists forever per user)

const CONSENT_KEY = 'desifirms_cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment to re-ask consent after policy changes

/**
 * Get current consent status
 * @returns {object|null} Consent object or null if not set
 */
export const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored);

    // Check if consent version matches current version
    if (consent.version !== CONSENT_VERSION) {
      return null; // Re-ask if version changed
    }

    return consent;
  } catch (e) {
    return null;
  }
};

/**
 * Check if user has made a consent choice
 * @returns {boolean}
 */
export const hasConsentChoice = () => {
  return getConsent() !== null;
};

/**
 * Check if analytics tracking is allowed
 * @returns {boolean}
 */
export const isAnalyticsAllowed = () => {
  const consent = getConsent();
  return consent?.analytics === true;
};

/**
 * Check if marketing tracking is allowed
 * @returns {boolean}
 */
export const isMarketingAllowed = () => {
  const consent = getConsent();
  return consent?.marketing === true;
};

/**
 * Save user consent choice
 * @param {object} choices - { analytics: boolean, marketing: boolean }
 */
export const saveConsent = (choices) => {
  const consent = {
    version: CONSENT_VERSION,
    analytics: choices.analytics || false,
    marketing: choices.marketing || false,
    timestamp: new Date().toISOString(),
  };

  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

  // Dispatch custom event for other components to react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consent }));

  return consent;
};

/**
 * Accept all cookies
 */
export const acceptAll = () => {
  return saveConsent({ analytics: true, marketing: true });
};

/**
 * Decline all optional cookies (only essential)
 */
export const declineAll = () => {
  return saveConsent({ analytics: false, marketing: false });
};

/**
 * Clear consent (for testing or user request)
 */
export const clearConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
  window.dispatchEvent(new CustomEvent('cookieConsentCleared'));
};

const cookieConsent = {
  get: getConsent,
  hasChoice: hasConsentChoice,
  isAnalyticsAllowed,
  isMarketingAllowed,
  save: saveConsent,
  acceptAll,
  declineAll,
  clear: clearConsent,
};

export default cookieConsent;
